import { isEqual } from 'lodash';
import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { GridColDef, GridPinnedColumnFields } from '@mui/x-data-grid-pro';
import { ColumnDensity, SORT_ORDER } from '@common/types';

export type PersistedTeamColumn = Pick<
  GridColDef,
  | 'field'
  | 'headerName'
  | 'width'
  | 'minWidth'
  | 'flex'
  | 'sortable'
  | 'filterable'
  | 'disableColumnMenu'
  | 'align'
  | 'headerAlign'
>;

export const initialTeamColumns: PersistedTeamColumn[] = [
  {
    field: 'name',
    headerName: 'Name',
    minWidth: 200,
    flex: 1,
    sortable: true,
    filterable: false,
  },
  {
    field: 'description',
    headerName: 'Description',
    minWidth: 180,
    flex: 1,
    sortable: false,
    filterable: false,
  },
  {
    field: 'city',
    headerName: 'City / State',
    minWidth: 150,
    flex: 1,
    sortable: true,
    filterable: false,
  },
  {
    field: 'managing_entity',
    headerName: 'Managing Entity',
    minWidth: 180,
    flex: 1,
    sortable: false,
    filterable: false,
  },
  {
    field: 'other_assignments',
    headerName: 'Other Assignments',
    width: 160,
    minWidth: 160,
    sortable: false,
    filterable: false,
  },
  {
    field: 'user_count',
    headerName: 'Users',
    width: 100,
    minWidth: 100,
    sortable: true,
    filterable: false,
  },
  {
    field: 'lead_user',
    headerName: 'Lead User',
    minWidth: 160,
    flex: 1,
    sortable: true,
    filterable: false,
  },
  {
    field: 'patient_count',
    headerName: 'Patients',
    width: 110,
    minWidth: 110,
    sortable: true,
    filterable: false,
  },
];

export const initialTeamPinnedColumns: GridPinnedColumnFields = {
  left: [],
  right: [],
};

export type ColumnNames =
  | 'name'
  | 'city'
  | 'partner_names'
  | 'practice_names'
  | 'user_count'
  | 'lead_user'
  | 'patient_count';

export interface ITeamTableFilters {
  page: number;
  limit: number;
  search: string;
  sort_by: ColumnNames;
  sort_order: SORT_ORDER;
  state: string;
  record_id: string;
}

const getDefaultFilters = (): ITeamTableFilters => ({
  search: '',
  sort_order: 'asc',
  sort_by: 'name',
  page: 1,
  limit: 10,
  state: '',
  record_id: '',
});

type TKeyOfFilters = keyof ITeamTableFilters;

const getAppliedFiltersCount = (
  initialFiltersList: ITeamTableFilters,
  appliedFilters: ITeamTableFilters,
): number => {
  const filtersKeyList: TKeyOfFilters[] = Object.keys(appliedFilters).filter(
    key =>
      key !== 'page' &&
      key !== 'limit' &&
      key !== 'search' &&
      key !== 'sort_order' &&
      key !== 'sort_by',
  ) as TKeyOfFilters[];

  return filtersKeyList.reduce(
    (count, key) =>
      isEqual(initialFiltersList[key], appliedFilters[key]) ? count : count + 1,
    0,
  );
};

type TTeamTableStore = {
  filters: ITeamTableFilters;
  setFilters: (newFilters: Partial<ITeamTableFilters>) => void;
  clearStore: () => void;
  appliedFiltersCount: number;

  columns: PersistedTeamColumn[];
  setColumns: (columns: PersistedTeamColumn[]) => void;
  pinnedColumns: GridPinnedColumnFields;
  setPinnedColumns: (pinnedColumns: GridPinnedColumnFields) => void;
  hiddenColumns: string[];
  setHiddenColumns: (hiddenColumns: string[]) => void;
  density: ColumnDensity;
  setDensity: (density: ColumnDensity) => void;
  resetView: () => void;
};

export const useTeamTableStore = create<TTeamTableStore>()(
  persist(
    set => ({
      filters: getDefaultFilters(),
      setFilters: newFilters =>
        set(state => {
          const updatedFilters = { ...state.filters, ...newFilters };
          return {
            filters: updatedFilters,
            appliedFiltersCount: getAppliedFiltersCount(
              getDefaultFilters(),
              updatedFilters,
            ),
          };
        }),
      clearStore: () =>
        set(() => ({
          filters: getDefaultFilters(),
          appliedFiltersCount: 0,
        })),
      appliedFiltersCount: 0,

      columns: initialTeamColumns,
      setColumns: columns => set({ columns }),
      pinnedColumns: initialTeamPinnedColumns,
      setPinnedColumns: pinnedColumns => set({ pinnedColumns }),
      hiddenColumns: [],
      setHiddenColumns: hiddenColumns => set({ hiddenColumns }),
      density: 'comfortable',
      setDensity: density => set({ density }),
      resetView: () =>
        set({
          columns: initialTeamColumns,
          pinnedColumns: initialTeamPinnedColumns,
          hiddenColumns: [],
          density: 'comfortable',
        }),
    }),
    {
      name: 'team-table-store',
      partialize: state => ({
        columns: state.columns,
        pinnedColumns: state.pinnedColumns,
        hiddenColumns: state.hiddenColumns,
        density: state.density,
        sort_by: state.filters.sort_by,
        sort_order: state.filters.sort_order,
      }),
      merge: (persistedState, currentState) => {
        const persisted = (persistedState ?? {}) as Partial<
          TTeamTableStore & Pick<ITeamTableFilters, 'sort_by' | 'sort_order'>
        >;

        const mergedFilters: ITeamTableFilters = {
          ...currentState.filters,
          ...(persisted.sort_by && { sort_by: persisted.sort_by }),
          ...(persisted.sort_order && { sort_order: persisted.sort_order }),
        };

        return {
          ...currentState,
          ...persisted,
          filters: mergedFilters,
        };
      },
    },
  ),
);
